import React from 'react';
import { Box, Paper, Typography, Chip, TextField } from '@mui/material';
import FavoriteIcon from '@mui/icons-material/Favorite';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useWizard, HealthSupportInfo, HealthSupportAnswers } from '../../context/WizardContext';
import { useProgressTracking } from '../../hooks/useProgressTracking';

const GREEN = '#2BA784';

const DIET = ['Daily', 'Regularly', 'Unsure', 'Infrequent', 'Rarely'];
const COMMUNITY = ['Very closely connected', 'Connected', 'Unsure', 'Disconnected', 'Very disconnected'];
const CONDITIONS = ['Arthritis', 'Asthma', 'Cancer', 'Cardiac condition', 'Diabetes', 'Hypertension', 'Memory / cognitive', 'Mood disorder', "Parkinson's", 'Other'];
const RELY = ['Spouse / partner', 'Adult child', 'Sibling', 'Other family', 'Close friend', 'Paid caregiver', 'Not sure yet'];
const DEPEND = ['Spouse / partner', 'Parent', 'Adult child', 'Grandchild', 'Sibling', 'Friend', 'Special needs', 'No one currently'];

const EMPTY_HEALTH: HealthSupportAnswers = {
  healthConditions: [],
  relyOnForCare: [],
  dependOnYou: [],
  threeAmCall: {},
};

// Toggle a value in a multi-select list. If `exclusive` is provided, selecting it
// clears everything else (and selecting anything else clears it).
const toggleMulti = (list: string[], value: string, exclusive?: string): string[] => {
  const has = list.includes(value);
  if (exclusive && value === exclusive) {
    return has ? [] : [value];
  }
  let next = has ? list.filter((v) => v !== value) : list.concat(value);
  if (exclusive) next = next.filter((v) => v !== exclusive);
  return next;
};

// NOTE: these must live at module scope. Defining them inside HealthSupportStep
// gives them a new identity on every render, which remounts the subtree and makes
// the HQ6 name input lose focus after each keystroke.
const SelectChip: React.FC<{ label: string; selected: boolean; onClick: () => void }> = ({ label, selected, onClick }) => (
  <Chip
    label={label}
    onClick={onClick}
    icon={selected ? <CheckCircleIcon sx={{ color: '#fff !important' }} /> : undefined}
    sx={{
      m: 0.5,
      fontWeight: 500,
      cursor: 'pointer',
      bgcolor: selected ? GREEN : 'transparent',
      color: selected ? '#fff' : 'text.primary',
      border: `1px solid ${selected ? GREEN : 'rgba(0,0,0,0.23)'}`,
      '&:hover': { bgcolor: selected ? '#249573' : 'rgba(43,167,132,0.08)' },
    }}
  />
);

const QuestionCard: React.FC<{ title: string; subtitle?: string; children: React.ReactNode }> = ({ title, subtitle, children }) => (
  <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
    <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{title}</Typography>
    {subtitle && <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>{subtitle}</Typography>}
    <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>{children}</Box>
  </Paper>
);

interface Props {
  subject: 'primary' | 'partner';
}

const HealthSupportStep: React.FC<Props> = ({ subject }) => {
  const { wizardData, updateWizardData } = useWizard();
  useProgressTracking({
    stepName: subject === 'partner' ? 'Partner Health & Support' : 'Health & Support',
    stepNumber: subject === 'partner' ? 8 : 6,
  });

  const hasPartner = wizardData.personal.hasPartner;
  const hs: HealthSupportAnswers = wizardData.healthSupport?.[subject] || EMPTY_HEALTH;

  const update = (patch: Partial<HealthSupportAnswers>) => {
    const existing: HealthSupportInfo = wizardData.healthSupport || { primary: EMPTY_HEALTH };
    const current = existing[subject] || EMPTY_HEALTH;
    updateWizardData({
      healthSupport: {
        ...existing,
        [subject]: { ...current, ...patch },
        planningTogether: hasPartner,
      },
    });
  };

  // For a couple, personalize every question with THIS person's first name
  // (client1 or client2) instead of "you"; a solo client stays second-person.
  const subjectName = subject === 'partner'
    ? (wizardData.personal.client2?.firstName || 'your partner')
    : (wizardData.personal.client1?.firstName || 'you');
  const personalized = hasPartner;

  const headerTitle = personalized
    ? `A few quick questions about ${subjectName}'s health & support`
    : 'A few quick questions about your health & support';

  const titles = personalized ? {
    hq1: `How often does ${subjectName} eat healthy, balanced meals?`,
    hq2: `How connected does ${subjectName} feel with their community?`,
    hq3: `Is ${subjectName} managing any health conditions?`,
    hq4: `Who might ${subjectName} rely on for care, now or later?`,
    hq5: `Who might depend on ${subjectName} for care?`,
    hq6: `Who’s ${subjectName}’s 3 AM call?`,
    hq6Subtitle: `The one person ${subjectName} would reach in an urgent moment.`,
  } : {
    hq1: 'How often do you eat healthy, balanced meals?',
    hq2: 'How connected do you feel with your community?',
    hq3: 'Are you managing any health conditions?',
    hq4: 'Who might you rely on for care, now or later?',
    hq5: 'Who might depend on you for care?',
    hq6: 'Who’s your 3 AM call?',
    hq6Subtitle: 'The one person you’d reach in an urgent moment.',
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, maxWidth: 600, mx: 'auto' }}>
      <Box sx={{ textAlign: 'center' }}>
        <Chip
          icon={<FavoriteIcon sx={{ color: `${GREEN} !important` }} />}
          label="HEALTH & SUPPORT"
          sx={{ bgcolor: 'rgba(43,167,132,0.12)', color: GREEN, fontWeight: 700, letterSpacing: 0.5 }}
        />
        <Typography variant="h6" fontWeight="bold" sx={{ mt: 2 }}>
          {headerTitle}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          About a minute — there are no wrong answers. These help your advisor plan for a long life.
        </Typography>
      </Box>

      {/* HQ1 */}
      <QuestionCard title={titles.hq1}>
        {DIET.map((o) => (
          <SelectChip key={o} label={o} selected={hs.healthyMeals === o} onClick={() => update({ healthyMeals: o })} />
        ))}
      </QuestionCard>

      {/* HQ2 */}
      <QuestionCard title={titles.hq2}>
        {COMMUNITY.map((o) => (
          <SelectChip key={o} label={o} selected={hs.communityConnection === o} onClick={() => update({ communityConnection: o })} />
        ))}
      </QuestionCard>

      {/* HQ3 */}
      <QuestionCard
        title={titles.hq3}
        subtitle="Select any that apply — or choose “None of these” to move on."
      >
        <SelectChip
          label="None of these"
          selected={!!hs.healthConditionsNone}
          onClick={() => update({ healthConditionsNone: !hs.healthConditionsNone, healthConditions: [] })}
        />
        {CONDITIONS.map((o) => (
          <SelectChip
            key={o}
            label={o}
            selected={hs.healthConditions.includes(o)}
            onClick={() => update({ healthConditions: toggleMulti(hs.healthConditions, o), healthConditionsNone: false })}
          />
        ))}
      </QuestionCard>

      {/* HQ4 */}
      <QuestionCard
        title={titles.hq4}
        subtitle="Tap all that apply."
      >
        {RELY.map((o) => (
          <SelectChip
            key={o}
            label={o}
            selected={hs.relyOnForCare.includes(o)}
            onClick={() => update({ relyOnForCare: toggleMulti(hs.relyOnForCare, o, 'Not sure yet') })}
          />
        ))}
      </QuestionCard>

      {/* HQ5 */}
      <QuestionCard
        title={titles.hq5}
        subtitle="Tap all that apply."
      >
        {DEPEND.map((o) => (
          <SelectChip
            key={o}
            label={o}
            selected={hs.dependOnYou.includes(o)}
            onClick={() => update({ dependOnYou: toggleMulti(hs.dependOnYou, o, 'No one currently') })}
          />
        ))}
      </QuestionCard>

      {/* HQ6 — name only, matching HealthSpan's interaction model */}
      <QuestionCard
        title={titles.hq6}
        subtitle={titles.hq6Subtitle}
      >
        <TextField
          placeholder="Their name"
          value={hs.threeAmCall.name || ''}
          onChange={(e) => update({ threeAmCall: { ...hs.threeAmCall, name: e.target.value } })}
          size="small"
          fullWidth
        />
      </QuestionCard>
    </Box>
  );
};

export default HealthSupportStep;
